import React, { useState } from "react";
import { Card, Form } from "react-bootstrap";
import { Trash2 } from "lucide-react";
import { useApiMutation, refetch, onSuccess } from "../../utils/api";
import EditIdeaModal from "./edit_idea_modal";
import { useModal } from "../../utils/modal_launcher";
import { isEmpty } from "lodash";

type VoteType = "like" | "meh" | "dislike" | "approve";

interface IdeaCardProps {
  idea: {
    id: string;
    title: string;
    image: string;
    cost: number;
    link: string;
    votes_summary: {
      self: VoteType | null;
      like: string[];
      meh: string[];
      dislike: string[];
      approve: string[];
    };
    comments: {
      id: string;
      comment: string;
      user: { name: string };
      created_at: Date;
    }[];
  };
  trip: any;
  groups: { title: string }[];
}

export const IdeaCard = ({ idea, trip, groups }: IdeaCardProps) => {
  const { post } = useApiMutation();
  const [newComment, setNewComment] = useState("");
  const [objectFitAlt, setObjectFitAlt] = useState(false);
  const editIdeaModal = useModal(EditIdeaModal, {});

  const handleVote = (vote_type: VoteType) => {
    post(`/api/user/toggle_vote/${idea.id}`, { vote_type }).then(
      onSuccess(() => refetch("idea-board"))
    );
  };

  const addComment = (e: React.FormEvent) => {
    e.preventDefault();

    post(`/api/user/add_idea_comment/${idea.id}`, { comment: newComment }).then(
      onSuccess(() => {
        refetch("idea-board");
        setNewComment("");
      })
    );
  };

  const deleteComment = (commentId: string) => {
    if (confirm("Delete this comment?")) {
      post(`/api/user/delete_idea_comment/${commentId}`).then(
        onSuccess(() => refetch("idea-board"))
      );
    }
  };

  return (
    <Card>
      {idea.image && (
        <div className="position-relative hover-show-parent">
          <Card.Img
            variant="top"
            src={idea.image}
            loading="lazy"
            style={{
              height: 150,
              objectFit: objectFitAlt ? "contain" : "cover",
            }}
          />
          <div className="hover-show-child position-absolute top-5px right-5px">
            <a
              href={idea.link}
              target="_blank"
              rel="noopener noreferrer"
              className="idea-action-button soft-black"
            >
              <i className="fas fa-external-link-alt" />
            </a>
            <div
              className="idea-action-button mt-1"
              onClick={() => setObjectFitAlt(!objectFitAlt)}
            >
              <i className="fas fa-search" />
            </div>
          </div>
        </div>
      )}
      <Card.Body className="p-2">
        <div className="d-flex justify-content-between align-items-start">
          <a
            className="fs-17 black no-underline"
            href={idea.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {idea.title}
          </a>
        </div>
        {!isEmpty(idea.cost) && (
          <div className="bg-light-gray d-inline-block rounded p-1 mt-1">
            {formatCost(idea.cost)}
          </div>
        )}

        <div className="d-flex gap-3 justify-content-around mt-2">
          {[
            ["dislike", "far fa-frown", "fas blue", "gray"],
            ["meh", "far fa-meh", "fas blue", "gray"],
            ["like", "far fa-grin", "fas blue", "gray"],
            ["approve", "fas fa-check", "green", "gray-a"],
          ].map(([type, icon, active, inactive]) => {
            const isActive = idea.votes_summary.self === type;
            return (
              <button
                key={type}
                className="btn-text flex-1 bg-white d-flex align-items-center"
                onClick={() => handleVote(type as "like" | "meh" | "dislike" | "approve")}
              >
                <i className={`${icon} ${isActive ? active : inactive}`} />
                <span className="fs-12 ml-1 gray">
                  {idea.votes_summary[type as "like" | "meh" | "dislike" | "approve"]
                    .length || ""}
                </span>
              </button>
            );
          })}
          <button
            className="btn-text flex-1 bg-white"
            onClick={() => editIdeaModal.show({ idea, trip, groups })}
          >
            <i className="far fa-edit" />
          </button>
        </div>

        <div className="mt-2">
          {idea.idea_comments.map((comment) => (
            <div key={comment.id} className="mb-2 p-2 bg-light rounded fs-14">
              <div className="fs-12">
                <span className="fw-bold">{comment.user.name}:</span>
                <span className="fs-14 ml-1">{comment.comment}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center fs-12 text-muted mt-1">
                <span>{formatDate(comment.created_at)}</span>
                <button
                  className="btn-text fs-10"
                  onClick={() => deleteComment(comment.id)}
                >
                  <Trash2 className="gray" size={10} />
                </button>
              </div>
            </div>
          ))}
          <Form onSubmit={addComment} className="mt-3">
            <input
              type="text"
              placeholder="Add comment"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="subtle-input w-100"
            />
          </Form>
        </div>
      </Card.Body>
    </Card>
  );
};

const formatDate = (date?: Date) =>
  date
    ? new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const formatCost = (cost: number) => {
  if (cost.toString().match(/^\d+(\.\d{2})?$/)) {
    return `$${parseFloat(cost.toString()).toLocaleString()}`;
  } else {
    return cost;
  }
};
